import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserCircle, KeyRound, LogOut, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { API } from './shared';

export function ProfileMenu({ theme }) {
  const light = theme === 'light';
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'profile' | 'password'
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate('/auth');
  };

  const itemCls = `w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium text-left transition-colors ${
    light ? 'text-gray-600 hover:bg-gray-50 hover:text-gray-900' : 'text-white/60 hover:bg-white/5 hover:text-white'}`;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} data-testid="profile-menu-btn"
        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors ${light ? 'hover:bg-gray-100' : 'hover:bg-white/5'}`}>
        <span className={`text-xs ${light ? 'text-gray-500' : 'text-white/40'}`}>{user?.name}</span>
        <div className="w-7 h-7 rounded-full overflow-hidden bg-[#E9641A]/20 flex items-center justify-center text-[#E9641A] font-bold text-xs">
          {user?.profiles?.[0]?.avatar_url ? <img src={user.profiles[0].avatar_url} alt="" className="w-full h-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
        </div>
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''} ${light ? 'text-gray-400' : 'text-white/30'}`} />
      </button>
      {open && (
        <div data-testid="profile-menu-dropdown"
          className={`absolute right-0 top-full mt-1.5 w-56 rounded-xl border overflow-hidden shadow-xl z-50 ${
            light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`}>
          <div className={`px-3 py-2.5 border-b ${light ? 'border-gray-100' : 'border-white/5'}`}>
            <p className={`text-xs font-bold ${light ? 'text-gray-800' : 'text-white'}`}>{user?.name}</p>
            <p className={`text-[10px] ${light ? 'text-gray-400' : 'text-white/35'}`}>{user?.email}{user?.is_superadmin && <span className="ml-1.5 text-amber-400 font-bold uppercase">Superadmin</span>}</p>
          </div>
          <button onClick={() => { setModal('profile'); setOpen(false); }} className={itemCls} data-testid="profile-menu-infos">
            <UserCircle size={15} className="text-[#E9641A]" /> Infos du profil
          </button>
          <button onClick={() => { setModal('password'); setOpen(false); }} className={itemCls} data-testid="profile-menu-password">
            <KeyRound size={15} className="text-[#E9641A]" /> Modifier le mot de passe
          </button>
          <button onClick={doLogout} className={`${itemCls} !text-red-400 hover:!bg-red-500/10`} data-testid="profile-menu-logout">
            <LogOut size={15} /> Se déconnecter
          </button>
        </div>
      )}
      {modal === 'profile' && <ProfileModal light={light} user={user} onClose={() => setModal(null)} onSaved={checkAuth} />}
      {modal === 'password' && <PasswordModal light={light} onClose={() => setModal(null)} />}
    </div>
  );
}

const inputCls = (light, disabled) => `w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${
  light ? 'bg-white border border-gray-200 text-gray-800' : 'bg-white/5 border border-white/8 text-white'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;
const lblCls = (light) => `block text-[10px] mb-1 uppercase tracking-wider ${light ? 'text-gray-400' : 'text-white/35'}`;

function Modal({ light, title, onClose, children, testid }) {
  return createPortal(
    <div data-portal="true" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className={`rounded-xl border w-full max-w-md p-6 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className={`text-base font-bold ${light ? 'text-gray-900' : 'text-white'}`}>{title}</h3>
          <button onClick={onClose} className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'}><X size={17} /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function ProfileModal({ light, user, onClose, onSaved }) {
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatar, setAvatar] = useState(user?.profiles?.[0]?.avatar_url || '');
  const [gallery, setGallery] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [saving, setSaving] = useState(false);
  const canEmail = !!user?.is_superadmin;

  useEffect(() => {
    axios.get(`${API}/avatar-gallery`, { withCredentials: true })
      .then(r => setGallery((r.data || []).flatMap(c => c.avatars || []))).catch(() => {});
  }, []);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { first_name: firstName, last_name: lastName };
      if (canEmail && email !== user.email) payload.email = email;
      if (avatar !== (user?.profiles?.[0]?.avatar_url || '')) payload.avatar_url = avatar;
      await axios.put(`${API}/admin/me/profile`, payload, { withCredentials: true });
      toast.success('Profil mis à jour');
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  return (
    <Modal light={light} title="Infos du profil" onClose={onClose} testid="profile-modal">
      <div className="space-y-3 mb-5">
        <div>
          <label className={lblCls(light)}>Avatar du profil</label>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#E9641A]/20 flex items-center justify-center flex-none">
              {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" data-testid="profile-avatar-preview" />
                : <span className="text-[#E9641A] font-bold text-xl">{user?.name?.[0]?.toUpperCase()}</span>}
            </div>
            <button onClick={() => setShowGallery(g => !g)} data-testid="profile-avatar-change-btn"
              className="text-xs font-semibold text-[#E9641A] hover:text-[#FE975E]">
              {showGallery ? 'Masquer la galerie' : 'Changer mon avatar'}
            </button>
          </div>
          {showGallery && (
            <div className={`mt-2 grid grid-cols-6 gap-1.5 max-h-40 overflow-y-auto rounded-lg border p-2 ${light ? 'border-gray-200 bg-gray-50' : 'border-white/10 bg-white/[0.02]'}`} data-testid="profile-avatar-gallery">
              {gallery.map((url, i) => (
                <button key={i} onClick={() => setAvatar(url)} data-testid={`profile-avatar-opt-${i}`}
                  className={`rounded-lg overflow-hidden border-2 transition-colors ${avatar === url ? 'border-[#E9641A]' : 'border-transparent hover:border-white/30'}`}>
                  <img src={url} alt="" className="w-full aspect-square object-cover" />
                </button>
              ))}
              {gallery.length === 0 && <p className={`col-span-6 text-[11px] text-center py-3 ${light ? 'text-gray-400' : 'text-white/35'}`}>Aucun avatar dans la galerie</p>}
            </div>
          )}
        </div>
        <div><label className={lblCls(light)}>Prénom</label>
          <input className={inputCls(light)} value={firstName} onChange={e => setFirstName(e.target.value)} data-testid="profile-firstname" /></div>
        <div><label className={lblCls(light)}>Nom</label>
          <input className={inputCls(light)} value={lastName} onChange={e => setLastName(e.target.value)} data-testid="profile-lastname" /></div>
        <div><label className={lblCls(light)}>Email {!canEmail && <span className="normal-case">(modifiable uniquement par le superadmin)</span>}</label>
          <input className={inputCls(light, !canEmail)} value={email} disabled={!canEmail} onChange={e => setEmail(e.target.value)} data-testid="profile-email" /></div>
      </div>
      <button onClick={save} disabled={saving} data-testid="profile-save-btn"
        className="w-full bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-colors disabled:opacity-40">
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </Modal>
  );
}

function PasswordModal({ light, onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (saving) return;
    if (next.length < 6) return toast.error('Nouveau mot de passe : 6 caractères minimum');
    if (next !== confirm) return toast.error('Les mots de passe ne correspondent pas');
    setSaving(true);
    try {
      await axios.post(`${API}/admin/me/password`, { current_password: current, new_password: next }, { withCredentials: true });
      toast.success('Mot de passe modifié');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  return (
    <Modal light={light} title="Modifier le mot de passe" onClose={onClose} testid="password-modal">
      <div className="space-y-3 mb-5">
        <div><label className={lblCls(light)}>Mot de passe actuel</label>
          <input type="password" className={inputCls(light)} value={current} onChange={e => setCurrent(e.target.value)} data-testid="password-current" /></div>
        <div><label className={lblCls(light)}>Nouveau mot de passe</label>
          <input type="password" className={inputCls(light)} value={next} onChange={e => setNext(e.target.value)} data-testid="password-new" /></div>
        <div><label className={lblCls(light)}>Confirmer le nouveau mot de passe</label>
          <input type="password" className={inputCls(light)} value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="password-confirm" /></div>
      </div>
      <button onClick={save} disabled={saving} data-testid="password-save-btn"
        className="w-full bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-colors disabled:opacity-40">
        {saving ? 'Enregistrement…' : 'Modifier le mot de passe'}
      </button>
    </Modal>
  );
}
